/**
 * Currency Utility Functions
 * ==========================
 * Centralized currency formatting and symbol utilities for AkountFly.
 * Import these functions instead of duplicating currency logic across components.
 */

// Comprehensive currency symbol map
const CURRENCY_SYMBOLS = {
  NGN: '₦',
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  INR: '₹',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'CHF',
  KES: 'KSh',
  GHS: '₵',
  ZAR: 'R',
  AED: 'د.إ',
  SAR: '﷼',
  BRL: 'R$',
  MXN: 'MX$',
  SGD: 'S$',
  HKD: 'HK$',
  NZD: 'NZ$',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  RUB: '₽',
  TRY: '₺',
  THB: '฿',
  MYR: 'RM',
  IDR: 'Rp',
  PHP: '₱',
  VND: '₫',
  KRW: '₩',
  TWD: 'NT$',
  PKR: '₨',
  BDT: '৳',
  EGP: 'E£',
  MAD: 'MAD',
  TZS: 'TSh',
  UGX: 'USh',
  RWF: 'FRw',
  XOF: 'CFA',
  XAF: 'FCFA',
  BWP: 'P',
  MUR: '₨',
  SCR: '₨',
  ZMW: 'ZK',
  MWK: 'MK',
  ETB: 'Br',
  AOA: 'Kz',
  CDF: 'FC',
  NAD: 'N$'
};

/**
 * Get currency symbol for a given currency code.
 *
 * iter1091 - No hard-coded NGN default. If the caller passes a falsy
 * currency we return the empty string and emit a dev-only warning so
 * we can grep future silent misuses. Historically this defaulted to
 * '₦' which stamped every currency-agnostic call site with a Naira
 * symbol on foreign-currency businesses (root cause of Issue #3 on
 * Ore's USD business).
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'NGN', 'USD')
 * @returns {string} Currency symbol - or '' when no code is supplied.
 */
export const getCurrencySymbol = (currencyCode) => {
  if (!currencyCode) {
    if (typeof console !== 'undefined' && typeof process !== 'undefined' && process?.env?.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[currency] getCurrencySymbol called without a currency code');
    }
    return '';
  }
  const code = currencyCode.toUpperCase();
  return CURRENCY_SYMBOLS[code] || code;
};

/**
 * Get symbol from currency object or string
 * Handles both string currency codes and objects with currency property
 * @param {string|object} currencyOrBusiness - Currency code or business object
 * @returns {string} Currency symbol
 */
export const getSymbolFromCurrency = (currencyOrBusiness) => {
  if (!currencyOrBusiness) return '';

  // Handle string currency code
  if (typeof currencyOrBusiness === 'string') {
    return getCurrencySymbol(currencyOrBusiness);
  }

  // Handle object with currency property (like bill or invoice)
  if (typeof currencyOrBusiness === 'object') {
    const code = currencyOrBusiness.currency || currencyOrBusiness.currency_code;
    return code ? getCurrencySymbol(code) : '';
  }

  return '';
};

/**
 * Format amount with currency symbol.
 *
 * iter1091 - No hard-coded NGN default. When called without a currency
 * we render the raw number without a symbol and warn in dev. Every
 * call site should pass an explicit currency (the account's native
 * currency for a bank-native amount, or `business.currency` for a
 * functional-currency amount).
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - ISO 4217 currency code (required)
 * @param {object} options - Formatting options
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currencyCode, options = {}) => {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    showSymbol = true,
    locale = 'en-US'
  } = options;

  if (!currencyCode && typeof console !== 'undefined' && typeof process !== 'undefined' && process?.env?.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('[currency] formatCurrency called without a currency code');
  }

  const symbol = showSymbol ? getCurrencySymbol(currencyCode) : '';
  const num = parseFloat(amount) || 0;

  const formattedNumber = num.toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits
  });

  return `${symbol}${formattedNumber}`;
};

/**
 * Format a currency amount as a "native primary + functional sub-line"
 * pair (iter1091, Phase A of the multi-currency fix).
 *
 * Returned as an object so the caller can render it as two independent
 * pieces of JSX (bigger main line, smaller subline). Never renders the
 * functional line when the two currencies are identical.
 *
 * Example:
 *   const money = formatCurrencyPair(
 *     { native: 79, functional: 106.49, rate: 1.348, asAt: '2026-08-04' },
 *     { nativeCurrency: 'GBP', functionalCurrency: 'USD' }
 *   );
 *   // → { primary: '£79.00', secondary: '= $106.49 @ 1.348', hasSecondary: true }
 */
export const formatCurrencyPair = (
  { native, functional, rate, asAt } = {},
  { nativeCurrency, functionalCurrency } = {}
) => {
  const nc = (nativeCurrency || '').toUpperCase();
  const fc = (functionalCurrency || '').toUpperCase();
  const primary = formatCurrency(native, nc);
  if (!fc || nc === fc || functional == null) {
    return { primary, secondary: null, hasSecondary: false };
  }
  const secondaryAmount = formatCurrency(functional, fc);
  let secondary = `= ${secondaryAmount}`;
  if (rate) {
    secondary += ` @ ${Number(rate).toFixed(3)}`;
  }
  if (asAt) {
    secondary += ` on ${asAt.slice(0, 10)}`;
  }
  return { primary, secondary, hasSecondary: true };
};

/**
 * Format amount with sign prefix for positive/negative values
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code
 * @returns {string} Formatted string with +/- prefix
 */
export const formatAmountWithSign = (amount, currencyCode = 'NGN') => {
  const num = parseFloat(amount) || 0;
  const symbol = getCurrencySymbol(currencyCode);
  const absFormatted = Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  if (num > 0) {
    return `+${symbol}${absFormatted}`;
  } else if (num < 0) {
    return `-${symbol}${absFormatted}`;
  }
  return `${symbol}${absFormatted}`;
};

/**
 * Format amount for compact display (K, M, B, T suffixes).
 *
 * Used by KPI cards where very large balances (billions / trillions) would
 * otherwise overflow the card. Pair with a `title=` tooltip showing the
 * full precise value via `formatCurrency()`.
 *
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code
 * @param {object} options
 * @param {number} options.precision - Decimals for M/B/T (default 2)
 * @param {number} options.threshold - Min abs value to compact (default 1e6).
 *                                     Anything below renders as full currency.
 * @returns {string} Compact formatted string (e.g. "₦411.31M", "$-1.23B")
 */
export const formatCompactCurrency = (amount, currencyCode = 'NGN', options = {}) => {
  const { precision = 2, threshold = 1e6 } = options;
  const symbol = getCurrencySymbol(currencyCode);
  const num = parseFloat(amount) || 0;
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  // Small enough - use the standard full formatter so we never lose precision
  // unnecessarily on values that comfortably fit in the card.
  if (absNum < threshold) {
    return formatCurrency(num, currencyCode);
  }

  let value;
  let suffix;
  if (absNum >= 1e15) { value = absNum / 1e15; suffix = 'Q'; }   // Quadrillion
  else if (absNum >= 1e12) { value = absNum / 1e12; suffix = 'T'; } // Trillion
  else if (absNum >= 1e9)  { value = absNum / 1e9;  suffix = 'B'; } // Billion
  else if (absNum >= 1e6)  { value = absNum / 1e6;  suffix = 'M'; } // Million
  else                      { value = absNum / 1e3;  suffix = 'K'; } // Thousand

  // 1 decimal for K (4-digit space tight), `precision` for M/B/T/Q.
  const decimals = suffix === 'K' ? 1 : precision;
  // Strip trailing zeros (e.g. 411.30M → 411.3M; 1.00B → 1B).
  const trimmed = parseFloat(value.toFixed(decimals)).toString();
  return `${sign}${symbol}${trimmed}${suffix}`;
};

/**
 * Get all supported currencies with symbols
 * @returns {Array} Array of currency objects
 */
export const getAllCurrencies = () => {
  return Object.entries(CURRENCY_SYMBOLS).map(([code, symbol]) => ({
    code,
    symbol,
    label: `${code} (${symbol})`
  }));
};

/**
 * Check if a currency code is supported
 * @param {string} currencyCode - Currency code to check
 * @returns {boolean} True if supported
 */
export const isCurrencySupported = (currencyCode) => {
  if (!currencyCode) return false;
  return currencyCode.toUpperCase() in CURRENCY_SYMBOLS;
};

// Default export for convenience
export default {
  getCurrencySymbol,
  getSymbolFromCurrency,
  formatCurrency,
  formatCurrencyPair,
  formatAmountWithSign,
  formatCompactCurrency,
  getAllCurrencies,
  isCurrencySupported,
  CURRENCY_SYMBOLS
};
